import { Product1, Product2, Product3, Product4, Product5, Product7 } from 'img/imgconsts';
import React, { useMemo, useState } from 'react';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiInformationOutline } from '@mdi/js';

const Card = styled.div`
    background: ${(props) => props.theme.colors.surface};
    border: 1px solid ${(props) => props.theme.colors.border};
    border-radius: 12px;
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    gap: 3rem;

    @media (max-width: 768px) {
        flex-direction: column;
        padding: 1rem;
        margin: 0 0.5rem;
        align-items: center;
        gap: 2rem;
    }
`;

const SliderSection = styled.div`
    flex: 1;
    max-width: 550px;
    width: 100%;
    position: relative;

    @media (max-width: 768px) {
        max-width: 100%;
        width: 100%;
    }
`;

const ContentSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const SliderWrapper = styled.div`
    position: relative;
    margin: 0 40px;

    .slick-slider {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .slick-list {
        width: 100%;
        overflow: hidden;

        @media (max-width: 1000px) {
            width: 300px;
        }

        @media (max-width: 768px) {
            width: 260px;
        }
    }

    .slick-track {
        display: flex;
        align-items: center;
    }

    .slick-slide {
        width: 100% !important;

        & > div {
            width: 100%;
        }
    }

    .slick-dots {
        position: relative;
        bottom: 0;
        margin-top: 1rem;

        li {
            width: 80px;
            height: 80px;
            margin: 0 5px;

            @media (max-width: 768px) {
                width: 60px;
                height: 60px;
            }

            button {
                width: 80px;
                height: 80px;
                padding: 0;

                @media (max-width: 768px) {
                    width: 60px;
                    height: 60px;
                }

                &:before {
                    display: none;
                }
            }

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                border: 2px solid transparent;
                border-radius: 4px;
            }

            &.slick-active img {
                border-color: ${(props) => props.theme.colors.border};
            }
        }
    }

    /* Arrow styles */
    .slick-prev,
    .slick-next {
        width: 40px;
        height: 40px;
        z-index: 1;

        &:before {
            font-size: 40px;
            color: ${(props) => props.theme.colors.textPrimary};
            opacity: 0.5;
        }

        &:hover:before {
            opacity: 1;
        }
    }

    .slick-prev {
        left: -40px;
    }

    .slick-next {
        right: -40px;
    }

    @media (max-width: 768px) {
        margin: 0 30px;

        .slick-prev {
            left: -30px;
        }

        .slick-next {
            right: -30px;
        }

        .slick-prev,
        .slick-next {
            width: 25px;
            height: 25px;

            &:before {
                font-size: 25px;
            }
        }
    }
`;

const ProductImage = styled.img`
    width: 100%;
    max-height: 550px;
    height: auto;
    border-radius: 8px;
    object-fit: contain;

    @media (max-width: 768px) {
        max-height: 75vw;
    }
`;

const ThumbnailImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
`;

const ProductInfo = styled.div`
    color: ${(props) => props.theme.colors.textPrimary};
    text-align: left;
`;

const ProductTitle = styled.h2`
    font-size: 2rem;
    margin-bottom: 1rem;
`;

const ProductDescription = styled.p`
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
`;

const OrderInfo = styled.div`
    background: rgba(0, 0, 0, 0.2);
    padding: 20px;
    border-radius: 8px;
    margin-top: auto;
`;

const GiveawayInfo = styled(Card)`
    margin-top: 16px;
    margin-bottom: 2rem;
`;

// Removed custom layout; reuse Card/SliderSection/ContentSection for consistency

const GiveawayImage = styled.img`
    width: 100%;
    max-width: 320px;
    height: auto;
    border-radius: 8px;
    object-fit: contain;
    @media (max-width: 768px) {
        max-width: 100%;
    }
`;

const FormSection = styled.form`
    margin-top: 1rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 16px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const FormRow = styled.div`
    display: flex;
    flex-direction: column;
`;

const Label = styled.label`
    font-size: 0.95rem;
    margin-bottom: 6px;
`;

const Input = styled.input`
    padding: 10px 12px;
    border: 1px solid ${(props) => props.theme.colors.border};
    background: ${(props) => props.theme.colors.surface};
    color: ${(props) => props.theme.colors.textPrimary};
    border-radius: 6px;
`;

const Select = styled.select`
    padding: 10px 12px;
    border: 1px solid ${(props) => props.theme.colors.border};
    background: ${(props) => props.theme.colors.surface};
    color: ${(props) => props.theme.colors.textPrimary};
    border-radius: 6px;
`;

const SubmitButton = styled.button`
    margin-top: 12px;
    grid-column: 1 / -1;
    padding: 12px 16px;
    border-radius: 6px;
    border: none;
    background: ${(props) => props.theme.colors.primary};
    color: #fff;
    font-weight: 600;
    cursor: pointer;
    opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const Backdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalCard = styled.div`
    background: ${(props) => props.theme.colors.surface};
    color: ${(props) => props.theme.colors.textPrimary};
    border: 1px solid ${(props) => props.theme.colors.border};
    border-radius: 12px;
    max-width: 560px;
    width: calc(100% - 32px);
    padding: 24px;
`;

const ModalActions = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 16px;
`;

const ModalButton = styled.button`
    margin-top: 12px;
    grid-column: 1 / -1;
    padding: 12px 16px;
    border-radius: 6px;
    border: none;
    background: ${(props) => props.theme.colors.primary};
    color: #fff;
    font-weight: 600;
    cursor: pointer;
`;

const PayPalContainer = styled.div<{ $status: 'idle' | 'processing' | 'success' | 'error' }>`
    background: ${(props) => (props.$status === 'success' ? 'rgba(56, 142, 60, 0.12)' : props.theme.colors.surface)};
    color: ${(props) => props.theme.colors.textPrimary};
    border: 1px solid ${(props) => props.theme.colors.border};
    border-radius: 8px;
    padding: 12px;
`;

const US_STATES = [
    'AL',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY'
];

const ProductSlider = () => {
    const [size, setSize] = useState('M');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    const [city, setCity] = useState('');
    const [stateRegion, setStateRegion] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const country = 'US';
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
    const [paymentError, setPaymentError] = useState<string>('');
    const [paypalOrderId, setPaypalOrderId] = useState<string>('');
    const UNIT_PRICE = process.env.REACT_APP_SHIRT_PRICE || '30.00';

    const isValid = useMemo(() => {
        return (
            !!size && !!firstName && !!lastName && !!address1 && !!city && !!stateRegion && !!postalCode && !!country
        );
    }, [size, firstName, lastName, address1, city, stateRegion, postalCode, country]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setFormSubmitted(true);
    };
    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        customPaging: function (i: number) {
            const images = [Product1, Product7, Product2, Product3, Product4, Product5];
            return (
                <a href={`#product-${i + 1}`}>
                    <ThumbnailImage src={images[i]} alt={`Thumbnail ${i + 1}`} />
                </a>
            );
        }
    };

    return (
        <>
            <GiveawayInfo>
                <ContentSection>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Icon path={mdiInformationOutline} size={1} color={'currentColor'} />
                        <h3 style={{ margin: 0 }}>Promotions</h3>
                    </div>
                    <p style={{ marginTop: 8, fontWeight: 600 }}>More promotions coming soon!</p>
                    <p style={{ marginTop: 4 }}>
                        Our last giveaway ended on <strong>December 10</strong>. Pre-order shirts from that promotion
                        are now in production and will ship within approximately 3–4 weeks from the end of the
                        promotion.
                    </p>
                </ContentSection>
            </GiveawayInfo>

            {/**<Card>
                <SliderSection>
                    <SliderWrapper>
                        <Slider {...settings}>
                            <div>
                                <ProductImage src={Product1} alt="Shirt Photo 1" />
                            </div>
                            <div>
                                <ProductImage src={Product7} alt="Shirt Photo 6" />
                            </div>
                            <div>
                                <ProductImage src={Product2} alt="Shirt Photo 2" />
                            </div>
                            <div>
                                <ProductImage src={Product3} alt="Shirt Photo 3" />
                            </div>
                            <div>
                                <ProductImage src={Product4} alt="Shirt Photo 4" />
                            </div>
                            <div>
                                <ProductImage src={Product5} alt="Shirt Photo 5" />
                            </div>
                        </Slider>
                    </SliderWrapper>
                </SliderSection>

                <ContentSection>
                    <ProductInfo>
                        <ProductTitle>Forging Brains Shirt</ProductTitle>
                        <ProductDescription>
                            Premium tee for fans of the Forging Brains Podcast. Comfortable fit, soft fabric, and a
                            clean design you can wear anywhere. <strong>Shirts will ship after the giveaway.</strong>
                        </ProductDescription>

                        <OrderInfo>
                            <h3>Price</h3>
                            <p>${UNIT_PRICE} USD</p>
                            <p>Fill out your details and continue to PayPal.</p>

                            <FormSection onSubmit={handleSubmit}>
                                <FormRow>
                                    <Label htmlFor="size">Size</Label>
                                    <Select id="size" value={size} onChange={(e) => setSize(e.target.value)}>
                                        <option value="S">S</option>
                                        <option value="M">M</option>
                                        <option value="L">L</option>
                                        <option value="XL">XL</option>
                                        <option value="XXL">XXL</option>
                                        <option value="3XL">3XL</option>
                                        <option value="4XL">4XL</option>
                                        <option value="5XL">5XL</option>
                                    </Select>
                                </FormRow>

                                <FormRow>
                                    <Label htmlFor="firstName">First Name</Label>
                                    <Input
                                        id="firstName"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                    />
                                </FormRow>

                                <FormRow>
                                    <Label htmlFor="lastName">Last Name</Label>
                                    <Input
                                        id="lastName"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                    />
                                </FormRow>

                                <FormRow>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </FormRow>

                                <FormRow>
                                    <Label htmlFor="address1">Address Line 1</Label>
                                    <Input
                                        id="address1"
                                        value={address1}
                                        onChange={(e) => setAddress1(e.target.value)}
                                    />
                                </FormRow>

                                <FormRow>
                                    <Label htmlFor="address2">Address Line 2 (optional)</Label>
                                    <Input
                                        id="address2"
                                        value={address2}
                                        onChange={(e) => setAddress2(e.target.value)}
                                    />
                                </FormRow>

                                <FormRow>
                                    <Label htmlFor="city">City</Label>
                                    <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} />
                                </FormRow>

                                <FormRow>
                                    <Label htmlFor="state">State (US)</Label>
                                    <Select
                                        id="state"
                                        value={stateRegion}
                                        onChange={(e) => setStateRegion(e.target.value)}
                                    >
                                        <option value="">Select state</option>
                                        {US_STATES.map((st) => (
                                            <option key={st} value={st}>
                                                {st}
                                            </option>
                                        ))}
                                    </Select>
                                </FormRow>

                                <FormRow>
                                    <Label htmlFor="postal">Postal Code</Label>
                                    <Input
                                        id="postal"
                                        value={postalCode}
                                        onChange={(e) => setPostalCode(e.target.value)}
                                    />
                                </FormRow>

                                <FormRow>
                                    <Label htmlFor="country">Country</Label>
                                    <Input id="country" value="US" disabled />
                                    <span style={{ fontSize: '0.9rem', opacity: 0.85, marginTop: 6 }}>
                                        US shipping only (contiguous 48 states)
                                    </span>
                                </FormRow>

                                {paymentStatus !== 'success' ? (
                                    <SubmitButton type="submit" disabled={!isValid || paymentStatus === 'processing'}>
                                        Continue to PayPal
                                    </SubmitButton>
                                ) : null}
                            </FormSection>

                            {!isValid && formSubmitted ? (
                                <p style={{ color: '#ffb4b4', marginTop: 8 }}>Please complete all required fields.</p>
                            ) : null}

                            {formSubmitted && isValid ? (
                                <div style={{ marginTop: 12 }}>
                                    <PayPalContainer $status={paymentStatus}>
                                        {paymentStatus !== 'success' ? (
                                            <PayPalButtons
                                                style={{ layout: 'horizontal', tagline: false }}
                                                createOrder={(data, actions) => {
                                                    setPaymentStatus('processing');
                                                    setPaymentError('');
                                                    return actions.order.create({
                                                        intent: 'CAPTURE',
                                                        purchase_units: [
                                                            {
                                                                description: `Forging Brains Shirt - Size ${size}`,
                                                                amount: {
                                                                    currency_code: 'USD',
                                                                    value: UNIT_PRICE
                                                                },
                                                                shipping: {
                                                                    name: {
                                                                        full_name: `${firstName} ${lastName}`.trim()
                                                                    },
                                                                    address: {
                                                                        address_line_1: address1,
                                                                        address_line_2: address2 || undefined,
                                                                        admin_area_2: city,
                                                                        admin_area_1: stateRegion,
                                                                        postal_code: postalCode,
                                                                        country_code: country || 'US'
                                                                    }
                                                                },
                                                                custom_id: `size:${size};email:${email || 'na'}`
                                                            }
                                                        ],
                                                        payer: {
                                                            name: {
                                                                given_name: firstName,
                                                                surname: lastName
                                                            },
                                                            email_address: email || undefined
                                                        },
                                                        application_context: {
                                                            shipping_preference: 'SET_PROVIDED_ADDRESS'
                                                        }
                                                    });
                                                }}
                                                onApprove={(data, actions) => {
                                                    if (!actions.order) return Promise.resolve();
                                                    setPaymentStatus('processing');
                                                    setPaymentError('');
                                                    return actions.order
                                                        .capture()
                                                        .then((details) => {
                                                            setPaypalOrderId(details.id || '');
                                                            setPaymentStatus('success');
                                                            setIsConfirmationOpen(true);
                                                        })
                                                        .catch(() => {
                                                            setPaymentStatus('error');
                                                            setPaymentError(
                                                                'We could not complete the payment. Please try again.'
                                                            );
                                                        });
                                                }}
                                                onCancel={() => {
                                                    setPaymentStatus('idle');
                                                }}
                                                onError={(err) => {
                                                    setPaymentStatus('error');
                                                    setPaymentError(
                                                        'A payment error occurred. Please try again or use a different method.'
                                                    );
                                                    // Optional: console.error(err);
                                                }}
                                            />
                                        ) : null}
                                        {paymentStatus === 'processing' ? (
                                            <p style={{ marginTop: 8 }}>Processing payment…</p>
                                        ) : null}
                                        {paymentStatus === 'success' ? (
                                            <p style={{ marginTop: 8 }}>
                                                Thanks! Your order has been placed
                                                {paypalOrderId ? ` (Order ID: ${paypalOrderId})` : ''}.
                                            </p>
                                        ) : null}
                                        {paymentStatus === 'error' && paymentError ? (
                                            <p style={{ marginTop: 8, color: '#ffb4b4' }}>{paymentError}</p>
                                        ) : null}
                                        {paymentStatus === 'success' ? (
                                            <div style={{ marginTop: 8 }}>
                                                <ModalButton
                                                    onClick={() => {
                                                        setFormSubmitted(false);
                                                        setPaymentStatus('idle');
                                                        setPaymentError('');
                                                        setPaypalOrderId('');
                                                        setIsConfirmationOpen(false);
                                                    }}
                                                >
                                                    Start a new order
                                                </ModalButton>
                                            </div>
                                        ) : null}
                                    </PayPalContainer>
                                </div>
                            ) : null}

                            <p style={{ marginTop: 12 }}>
                                Questions? Feel free to <Link to="/contact">contact us</Link>!
                            </p>
                        </OrderInfo>
                    </ProductInfo>
                </ContentSection>
                {isConfirmationOpen ? (
                    <Backdrop onClick={() => setIsConfirmationOpen(false)}>
                        <ModalCard onClick={(e) => e.stopPropagation()}>
                            <h3>Order confirmed</h3>
                            <p style={{ marginTop: 8 }}>Thank you! Your payment was completed successfully.</p>
                            <div style={{ marginTop: 12 }}>
                                <strong>Order ID:</strong> {paypalOrderId || '—'}
                            </div>
                            <div style={{ marginTop: 8 }}>
                                <strong>Item:</strong> Forging Brains Shirt (Size {size})
                            </div>
                            <div style={{ marginTop: 8 }}>
                                <strong>Ship to:</strong>
                                <div>
                                    {firstName} {lastName}
                                </div>
                                <div>{address1}</div>
                                {address2 ? <div>{address2}</div> : null}
                                <div>
                                    {city}, {stateRegion} {postalCode}
                                </div>
                                <div>{country}</div>
                            </div>
                            {email ? (
                                <div style={{ marginTop: 8 }}>
                                    <strong>Email:</strong> {email}
                                </div>
                            ) : null}

                            <ModalActions>
                                <ModalButton
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        const summary = `Order ID: ${paypalOrderId}\nItem: Forging Brains Shirt (Size ${size})\nName: ${firstName} ${lastName}\nEmail: ${
                                            email || ''
                                        }\nAddress: ${address1}${
                                            address2 ? '\n' + address2 : ''
                                        }\n${city}, ${stateRegion} ${postalCode}\n${country}`;
                                        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                                            navigator.clipboard.writeText(summary);
                                        }
                                    }}
                                >
                                    Copy details
                                </ModalButton>
                                <ModalButton onClick={() => setIsConfirmationOpen(false)}>Close</ModalButton>
                            </ModalActions>
                        </ModalCard>
                    </Backdrop>
                ) : null}
            </Card>*/}
        </>
    );
};

export default ProductSlider;
